import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Routes from './Routes';
import { VERSION } from '../../version';
import { connect } from 'react-redux';
import { IAuthState, IRootState } from '../../@types/redux';
import AuthFunctions from '../../store/auth/functions';
import navFunctions from '../../store/nav/functions';
import { Icon, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import EnvHelper from '../../services/helper/envHelper';
import NavBar from '../customComponentV2/root/NavBar';
import NavDrawer from '../customComponentV2/root/NavDrawer';
import SideMenuItem from '../customComponentV2/menu/SideMenuItem';
import UserSettings from './UserSettings';
import { ISqlEmployee } from '../../@types/modelV2/rights/user/sqlEmployee';
import UserModelHelper from '../../@types/modelV2/rights/user/userModelHelper';
import { Link } from 'react-router-dom';


interface INavFrameProps {
    auth : IAuthState;
}

const NavFrame = (props : INavFrameProps) : React.ReactElement => {
    const location = useLocation();

    const [isUserSettingsDialogOpen, setIsUserSettingsDialogOpen] = useState<boolean>(false);
    const [isNavDrawerOpen, setIsNavDrawerOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const signOut = () : void => {
        AuthFunctions.logoutUser();
        closeDrawer();
    };

    const openUserSettings = async () : Promise<void> => {
        setIsUserSettingsDialogOpen(true);
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openDrawer = () : void => {
        setIsNavDrawerOpen(true);
    };

    const closeDrawer = () : void => {
        setIsNavDrawerOpen(false);
    };

    const closeUserSettings = () : void => {
        setIsUserSettingsDialogOpen(false);
    };

    const getItem = (itemKey : string, url: string, icon: React.ReactNode, listItemText: string) =>
    <Link
        key={'link' + url}
        to={url}
        onClick={closeDrawer}
        style={{ textDecoration: 'unset', color: 'unset' }}
    >
        <ListItem button key={itemKey}>
            <ListItemIcon>
                {icon}
            </ListItemIcon>
            <ListItemText primary={listItemText} />
        </ListItem>
    </Link>

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const { auth } = props;

    const currentUser = useMemo<ISqlEmployee | null>(() => {
        if (!auth.session?.employee) return null;

        return UserModelHelper.fromFirebaseToSqlEmployee(auth.session.employee);
    }, [auth.session]);

    const breadcrumbs = useMemo<Array<{ name : string; url : string }>>(() => {
        const pathname : string = location.pathname;
        const pathParameters : string = location.search;

        if (!pathname) {
            return [];
        }

        // Split by '/' and filter out empty strings (e.g., from leading/trailing slashes)
        const segments = pathname.split('/').filter(x => !!x);

        const result : Array<{ name : string; url : string }> = [];
        let currentPath = '';

        for (const segment of segments) {
            // Format segment name (camelCase/pascalCase to Title Case)
            // Add a space before any uppercase letter, then capitalize the first letter of the resulting string.
            var formattedName = segment
                .replace(/([A-Z])/g, ' $1') // Add space before uppercase letters
                .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter

            const lowerCaseSegment = segment.toLowerCase();

            if(lowerCaseSegment == 'lpu' || lowerCaseSegment == 'spu' || lowerCaseSegment == 'sps') {
                formattedName = formattedName.toUpperCase();
            }

            currentPath += `/${segment}`;
            result.push({
                url: currentPath,
                name: formattedName,
            });
        }

        return result;
    }, [location]);

    const renderSections = useMemo<React.ReactElement>(() => {
        return (
            <div>
                {
                    auth.session?.employee.IsBoreholeUser &&
                        <div>
                            <SideMenuItem
                                key={'borehole_nav_header'} 
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/borehole.svg`} />}
                                title={'Boreholes'}>
                                {
                                    getItem(
                                        'borehole_list_nav',
                                        '/borehole/list',
                                        <Icon>list</Icon>,
                                        'LIST'
                                    )
                                }
                                {
                                    getItem(
                                        'borehole_map_nav',
                                        '/borehole/map',
                                        <Icon>map</Icon>,
                                        'MAP'
                                    )
                                }
                                {
                                    getItem(
                                        'borehole_runtimes_override_nav',
                                        '/borehole/runtimesOverride',
                                        <Icon><img className='pl2' height={24} src={`${ASSET_BASE}/assets/images/icons/timer.svg`}/></Icon>,
                                        'RUNTIMES OVERRIDE'
                                    )
                                }

                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsFillingPointUser &&
                        <div>
                            <SideMenuItem
                                key={'chemical_filling_point_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/chemical_filling_point.svg`} />}
                                title={'Chemical Filling Points'}>
                                {
                                    getItem(
                                        'chemical_filling_point_list_nav',
                                        '/chemicalFillingPoint/list',
                                        <Icon>list</Icon>,
                                        'LIST'
                                    )
                                }
                                {
                                    getItem(
                                        'chemical_filling_point_map_nav',
                                        '/chemicalFillingPoint/map',
                                        <Icon>map</Icon>,
                                        'MAP'
                                    )
                                }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsDamUser &&
                        <div>
                            <SideMenuItem
                                key={'dam_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/dam.svg`} />}
                                title={'Dams'}>
                                {
                                    getItem(
                                        'dam_list_nav',
                                        '/dam/list',
                                        <Icon>list</Icon>,
                                        'LIST'
                                    )
                                }
                                {
                                    getItem(
                                        'dam_map_nav',
                                        '/dam/map',
                                        <Icon>map</Icon>,
                                        'MAP'
                                    )
                                }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsTransformerUser &&
                        <div>
                            <SideMenuItem
                                key={'transformer_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/transformer.svg`} />}
                                title={'Transformers'}>
                                    {
                                        getItem(
                                            'transformer_nav_header_list_nav',
                                            '/transformer/list',
                                            <Icon>list</Icon>,
                                            'LIST'
                                        )
                                    }
                                    {
                                        getItem(
                                            'transformer_nav_header_map_nav',
                                            '/transformer/map',
                                            <Icon>map</Icon>,
                                            'MAP'
                                        )
                                    }
                                    {
                                        auth.session.employee.IsTransformerAdmin &&
                                        getItem(
                                            'transformer_nav_accounts',
                                            '/transformer/bankAccounts',
                                            <Icon>account_balance</Icon>,
                                            'BANK ACCOUNTS'
                                        )
                                    }
                                    {
                                        auth.session.employee.IsTransformerTariffAdmin &&
                                        getItem(
                                            'trans_journal_nav',
                                            '/transformer/journals',
                                            <Icon>account_balance</Icon>,
                                            'JOURNALS'
                                        )
                                    }
                                    {
                                        auth.session.employee.IsTransformerTariffAdmin &&
                                        <SideMenuItem
                                            key={'transformer_nav_tariff_nav'}
                                            icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/transformerTariffs.svg`} />}
                                            title={'Tariff Management'}>
                                                {
                                                    getItem(
                                                        'LPU_admin_nav',
                                                        '/transformer/tariffs?type=lpu',
                                                        <div className={'h30 w30'} />,
                                                        'LPU'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'SPU_admin_nav',
                                                        '/transformer/tariffs?type=spu',
                                                        <div className={'h30 w30'} />,
                                                        'SPU'
                                                    )
                                                }
                                        </SideMenuItem>
                                    }
                                    {
                                        auth.session.employee.IsTransformerBudgetAdmin &&
                                        <SideMenuItem
                                            key={'transformer_nav_budget_nav'}
                                            icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/transformerTariffs.svg`} />}
                                            title={'Budget Management'}>
                                                {
                                                    getItem(
                                                        'LPU_budget_nav',
                                                        '/transformer/budget?type=lpu',
                                                        <div className={'h30 w30'} />,
                                                        'LPU'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'SPU_budget_nav',
                                                        '/transformer/budget?type=spu',
                                                        <div className={'h30 w30'} />,
                                                        'SPU'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'municipality_budget_nav',
                                                        '/transformer/budget?type=municipality',
                                                        <div className={'h30 w30'} />,
                                                        'Municipality'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'csv_budget_nav',
                                                        '/transformer/budget/csv',
                                                        <div className={'h30 w30'} />,
                                                        'CSV'
                                                    )
                                                }
                                        </SideMenuItem>
                                    }
                                <SideMenuItem
                                    key={'transformer_nav_capturing_nav'}
                                    icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/transformerTariffs.svg`} />}
                                    title={'Capturing'}>
                                        {
                                            auth.session.employee.IsTransformerLpuTariff &&
                                            getItem(
                                                'LPU_capturing_nav',
                                                '/transformer/capturing/lpu/invoice',
                                                <div className={'h30 w30'} />,
                                                'LPU Invoice'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsTransformerLpuTester &&
                                            getItem(
                                                'LPU_meter_nav',
                                                '/transformer/capturing/lpu/meter',
                                                <div className={'h30 w30'} />,
                                                'LPU Meter'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsTransformerSpuTariff &&
                                            getItem(
                                                'SPU_capturing_nav',
                                                '/transformer/capturing/spu/invoice',
                                                <div className={'h30 w30'} />,
                                                'SPU Invoice'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsTransformerSpuTester &&
                                            getItem(
                                                'SPU_meter_nav',
                                                '/transformer/capturing/spu/meter',
                                                <div className={'h30 w30'} />,
                                                'SPU Meter'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsMunicipalityInvoice &&
                                            getItem(
                                                'Municipality_invoice_nav',
                                                '/transformer/capturing/municipality/invoice',
                                                <div className={'h30 w30'} />,
                                                'Municipality Invoice'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsTransformerMunicipalityTester &&
                                            getItem(
                                                'Municipality_meter_nav',
                                                '/transformer/capturing/municipality/meter',
                                                <div className={'h30 w30'} />,
                                                'Municipality Meter'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsSPSInvoice &&
                                            getItem(
                                                'SPS_invoice_nav',
                                                '/transformer/capturing/sps/invoice',
                                                <div className={'h30 w30'} />,
                                                'SPS Invoice'
                                            )
                                        }
                                        {
                                            auth.session.employee.IsTransformerPrepaidTester &&
                                            getItem(
                                                'Prepaid_meter_nav',
                                                '/transformer/capturing/prepaid/meter',
                                                <div className={'h30 w30'} />,
                                                'Prepaid Amount'
                                            )
                                        }
                                </SideMenuItem>
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsWeatherStationUser &&
                        <div>
                            <SideMenuItem
                                key={'weather_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/weather.svg`} />}
                                title={'Weather Stations'}>
                                    {
                                        getItem(
                                            'weather_nav_header_list_nav',
                                            '/weather/list',
                                            <Icon>list</Icon>,
                                            'LIST'
                                        )
                                    }
                                    {
                                        getItem(
                                            'weather_nav_header_map_nav',
                                            '/weather/map',
                                            <Icon>map</Icon>,
                                            'MAP'
                                        )
                                    }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsRiverUser &&
                        <div>
                            <SideMenuItem
                                key={'river_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/rivers.svg`} />}
                                title={'Rivers'}>
                                    {
                                        getItem(
                                            'river_nav_header_list_nav',
                                            '/river/list',
                                            <Icon>list</Icon>,
                                            'LIST'
                                        )
                                    }
                                    {
                                        getItem(
                                            'river_nav_header_map_nav',
                                            '/river/map',
                                            <Icon>map</Icon>,
                                            'MAP'
                                        )
                                    }
                            </SideMenuItem>
                        </div>
                }
                {
                    (auth.session?.employee.IsRainMeterUser
                            || auth.session?.employee.IsRainMeterMeasurementUser
                            || auth.session?.employee.IsRainMeterAdmin) &&
                        <div>
                            <SideMenuItem
                                key={'rain_meter_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/rain_meter.svg`} />}
                                title={'Rain Meter'}>
                                    {
                                        getItem(
                                            'rain_meter_manager_nav',
                                            '/rainMeter/rainMeterManager',
                                            <Icon>list</Icon>,
                                            'LIST'
                                        )
                                    }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsPumpingStationUser &&
                        <div>
                            <SideMenuItem
                                key={'pumpingStation_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/pumpingStation.svg`} />}
                                title={'Pumping Stations'}>
                                    {
                                        getItem(
                                            'pumpingStation_nav_header_list_nav',
                                            '/pumpingStation/list',
                                            <Icon>list</Icon>,
                                            'LIST'
                                        )
                                    }
                                    {
                                        getItem(
                                            'pumpingStation_nav_header_map_nav',
                                            '/pumpingStation/map',
                                            <Icon>map</Icon>,
                                            'MAP'
                                        )
                                    }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsFlowmeterUser &&
                        <div>
                            <SideMenuItem
                                key={'flowmeter_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/flowmeter.svg`} />}
                                title={'Flowmeter'}>
                                    {
                                        getItem(
                                            'flowmeter_nav_header_list_nav',
                                            '/flowmeter/list',
                                            <Icon>list</Icon>,
                                            'LIST'
                                        )
                                    }
                                    {
                                        getItem(
                                            'flowmeter_nav_header_map_nav',
                                            '/flowmeter/map',
                                            <Icon>map</Icon>,
                                            'MAP'
                                        )
                                    }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsMasterDataAdmin &&
                        <div>
                            <SideMenuItem
                                key={'master_data_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/masterData.svg`} />}
                                title={'Master Data'}>
                                    {
                                        auth.session.employee.IsDivisionAdmin &&
                                            getItem(
                                                'division_nav',
                                                '/division',
                                                <Icon>list</Icon>,
                                                'Division'
                                            )
                                    }
                                    {
                                        auth.session.employee.IsCompanyAdmin &&
                                            getItem(
                                                'company_nav',
                                                '/masterData/company',
                                                <Icon>list</Icon>,
                                                'Company'
                                            )
                                    }
                                    {
                                        auth.session.employee.IsValueAddedTaxAdmin &&
                                            getItem(
                                                'vat_nav',
                                                '/masterData/valueAddedTax',
                                                <Icon>list</Icon>,
                                                'Value Added Tax'
                                            )
                                    }
                                    {
                                        auth.session.employee.IsGroundWaterZoneAdmin &&
                                            getItem(
                                                'ground_water_zone_nav',
                                                '/masterData/groundWaterZones',
                                                <Icon>list</Icon>,
                                                'Ground Water Zones'
                                            )
                                    }
                                    {
                                        auth.session.employee.IsBoreholeMasterDataAdmin &&
                                        <SideMenuItem
                                            key={'master_data_borehole_nav_header'}
                                            icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/borehole.svg`} />}
                                            title={'Borehole'}>
                                                {
                                                    getItem(
                                                        'borehole_issue_nav',
                                                        '/masterData/boreholeIssues',
                                                        <div/>,
                                                        'Issues'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'motor_nav',
                                                        '/masterData/motors',
                                                        <div/>,
                                                        'Motors'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'motor_size_nav',
                                                        '/masterData/motorSizes',
                                                        <div/>,
                                                        'Motor Sizes'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumps_nav',
                                                        '/masterData/pumps',
                                                        <div/>,
                                                        'Pumps'
                                                    )
                                                }
                                        </SideMenuItem>
                                    }
                                    {
                                        auth.session.employee.IsTransformerMasterDataAdmin &&
                                        <SideMenuItem
                                            key={'master_data_transformer_nav_header'}
                                            icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/transformer.svg`} />}
                                            title={'Transformer'}>
                                                {
                                                    getItem(
                                                        'transformer_group_nav',
                                                        '/masterData/transformer/group',
                                                        <div/>,
                                                        'Groups'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'transformer_usage_nav',
                                                        '/masterData/transformer/usage',
                                                        <div/>,
                                                        'Usage'
                                                    )
                                                }
                                        </SideMenuItem>
                                    }
                                    {
                                        auth.session.employee.IsPumpingStationAdmin &&
                                        <SideMenuItem
                                            key={'master_data_pumping_station_nav_header'}
                                            icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/pumpingStation.svg`} />}
                                            title={'Pumping Station'}>
                                                {
                                                    getItem(
                                                        'pumping_station_md_filter_size',
                                                        '/masterData/pumpingStation/filterSize',
                                                        <div/>,
                                                        'Filter Size'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_filter_type',
                                                        '/masterData/pumpingStation/filterType',
                                                        <div/>,
                                                        'Filter Type'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_flowmeter_make',
                                                        '/masterData/pumpingStation/flowmeterMake',
                                                        <div/>,
                                                        'Flowmeter Make'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_flowmeter_pulse',
                                                        '/masterData/pumpingStation/flowmeterPulse',
                                                        <div/>,
                                                        'Flowmeter Pulse'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_flowmeter_size',
                                                        '/masterData/pumpingStation/flowmeterSize',
                                                        <div/>,
                                                        'Flowmeter Size'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_motor_efficiency',
                                                        '/masterData/pumpingStation/motorEfficiency',
                                                        <div/>,
                                                        'Motor Efficiency'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_motor_make',
                                                        '/masterData/pumpingStation/motorMake',
                                                        <div/>,
                                                        'Motor Make'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_motor_mounting',
                                                        '/masterData/pumpingStation/motorMounting',
                                                        <div/>,
                                                        'Motor Mounting'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_motor_pole',
                                                        '/masterData/pumpingStation/motorPole',
                                                        <div/>,
                                                        'Motor Pole'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_motor_size',
                                                        '/masterData/pumpingStation/motorSize',
                                                        <div/>,
                                                        'Motor Size'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_motor_type',
                                                        '/masterData/pumpingStation/motorType',
                                                        <div/>,
                                                        'Motor Type'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_pump_make',
                                                        '/masterData/pumpingStation/pumpMake',
                                                        <div/>,
                                                        'Pump Make'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_pump_type',
                                                        '/masterData/pumpingStation/pumpType',
                                                        <div/>,
                                                        'Pump Type'
                                                    )
                                                }
                                                {
                                                    getItem(
                                                        'pumping_station_md_volute_casing_make',
                                                        '/masterData/pumpingStation/voluteCasingMake',
                                                        <div/>,
                                                        'Volute Casing Makes'
                                                    )
                                                }
                                        </SideMenuItem>
                                    }
                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsEmployeeAdmin &&
                        <div>
                            <SideMenuItem
                                key={'employee_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/rights.svg`} />}
                                title={'Rights'}>
                                    {
                                        getItem(
                                            'employee_nav',
                                            '/employee',
                                            <img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/users.svg`} />,
                                            'Employee'
                                        )
                                    }

                            </SideMenuItem>
                        </div>
                }
                {
                    auth.session?.employee.IsDevelopmentToolsAdmin &&
                        <div>
                            <SideMenuItem
                                key={'development_nav_header'}
                                icon={<img className={'h30 w30'} src={`${ASSET_BASE}/assets/images/icons/development.svg`} />}
                                title={'Development'}>
                                    {
                                        getItem(
                                            'development_tools_nav',
                                            '/development/tools',
                                            <div/>,
                                            'Tools'
                                        )
                                    }

                            </SideMenuItem>
                        </div>
                }
            </div>
        );
    }, [auth.session]);

    const userSettings = useMemo<React.ReactElement>(() => {
        if (currentUser) {
            return <UserSettings 
                isOpen={isUserSettingsDialogOpen}
                currentUser={currentUser ?? null}
                setIsUserDialogOpenCallback={(state : boolean) : void => setIsUserSettingsDialogOpen(state)}
                onClose={closeUserSettings}
                signOut={signOut}
            />;
        }

        return <div></div>;
    }, [isUserSettingsDialogOpen, currentUser]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return ( 
        <div className={'fdc hfill'}>
            <NavBar
                env={EnvHelper.getEnvName()}
                version={VERSION.version}
                currentUser={currentUser}
                signOut={signOut}
                breadcrumbs={breadcrumbs}
                openDrawer={openDrawer}
                closeDrawer={closeDrawer}
                openUserSettings={openUserSettings}
                closeUserSettings={closeUserSettings}
                isLatestVersion={true}
                userSettingsDialog={userSettings}
            />
            <div className={'fdr flx1 oyh'}>
                <NavDrawer
                    env={EnvHelper.getEnvName()} 
                    path={location.pathname}
                    currentUser={currentUser}
                    isOpen={isNavDrawerOpen}
                    isLoading={false}
                    sections={[renderSections]}
                    logOut={signOut}
                    closeDrawer={closeDrawer}
                    onHelpClick={() => {}}
                />
                <Routes />
            </div>
        </div>
    );

};

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
    };
};

export default connect(
    mapStateToProps,
)(NavFrame);
